import React, { useEffect, useState, useContext, useCallback } from "react";
import { useImmer } from "use-immer";
import { Formik, Form, Field, getIn, ErrorMessage } from "formik";
import * as Yup from "yup";
import moment from "moment";
import ErrorLine from "../elements/ErrorLine";
import LoadingIndicator from "../elements/LoadingIndicator";
import DispatchContext from "../../contexts/DispatchContext";
import DataFetcher from "../../utils/DataFetcher";
import formdata from "../../data/formdata";
import { dispError, dispSuccess, getResponse } from "../../utils/Tools";

function FormStatistics(props) {
  const appDispatch = useContext(DispatchContext);

  const isAdmin = () => props.userType === "super-admin";

  const fsValues = {
    licences: false,
    testuser: false
  };
  if (isAdmin()) fsValues.ikobe = false;

  const [fieldsets, setFieldsets] = useImmer(fsValues);

  const updateFieldset = (id, bool) => {
    setFieldsets(draft => {
      draft[id] = bool;
    });
  };

  const [clients, setClients] = useState(false);

  const loadData = useCallback(() => {
    DataFetcher.fetch(DataFetcher.CLIENT.LIST)
      .then(response => {
        if (!response.success) {
          return appDispatch(dispError(response.msg));
        }
        setClients(response.list);
      })
      .catch(error => {
        console.error(error);
      });
  }, [appDispatch, setClients]);

  useEffect(() => {
    if (!clients) loadData();
  }, [loadData, clients]);

  const clientTypeSelect = [
    ["all", "Alle"],
    ["einer", "Einer"],
    ["region", "Nach Bundesland"],
    ["clientType", "Nach Mandantenart"]
  ];

  const defaultValues = {
    licences: {
      date: {
        from: "",
        until: moment().format("YYYY-MM-DD")
      }
    },
    testuser: {
      date: {
        from: "",
        until: moment().format("YYYY-MM-DD")
      }
    }
  };
  const defClientVal = {
    type: clientTypeSelect[0][0],
    value: ""
  };
  if (isAdmin()) {
    defaultValues.ikobe = {
      date: {
        from: "",
        until: moment().format("YYYY-MM-DD")
      }
    };
    defaultValues.licences.client = defaultValues.testuser.client = defClientVal;
  }
  const onSubmit = (values, { resetForm, setSubmitting }) => {
    let payload = { ...values };

    if (isAdmin() && !fieldsets.ikobe) delete payload.ikobe;
    if (!fieldsets.licences) delete payload.licences;
    if (!fieldsets.testuser) delete payload.testuser;

    DataFetcher.fetch("/reports", payload)
      .then(response => {
        if (!response.success) {
          setSubmitting(false);
          return appDispatch(dispError(getResponse("016b")));
        }
        // appDispatch(dispSuccess(response.msg));
        appDispatch(dispSuccess(getResponse("016")));
        resetForm({ values: "" });
        // loadData();
      })
      .catch(error => {
        console.error(error);
        setSubmitting(false);
      });
  };

  const fieldsDisabled = () => !Boolean(Object.values(fieldsets).filter(value => value === true).length);
  const sendBtnDisabled = bool => (bool || fieldsDisabled() ? true : false);

  const yupObject = {};
  const yupDateProperty = Yup.object().shape(
    {
      from: Yup.date()
        .max(moment(), "Datum kann nicht in der Zukunft liegen.")
        .when("until", (until, schema) => until && schema.max(until, "Startdatum kann nicht später als Enddatum sein."))
        .required("Bitte geben Sie ein Startdatum ein."),
      until: Yup.date()
        .max(moment(), "Datum kann nicht in der Zukunft liegen")
        .when("from", (from, schema) => from && schema.min(from, "Enddatum kann nicht früher als Startdatum sein."))
        .required("Bitte geben Sie ein Enddatum ein.")
    },
    ["from", "until"]
  );
  const yupClientProperty = Yup.object().shape(
    {
      type: Yup.string().required("Bitte wählen Sie eine Mandantenoption aus."),
      value: Yup.mixed().when("type", {
        is: value => value !== "all",
        then: Yup.string().required("Bitte wählen Sie eine zweite Mandantenoption aus.")
      })
    },
    ["type", "value"]
  );

  if (isAdmin() && fieldsets.ikobe) {
    yupObject.ikobe = Yup.object().shape({
      date: yupDateProperty
    });
  }
  if (fieldsets.licences) {
    yupObject.licences = Yup.object().shape({
      date: yupDateProperty,
      client: isAdmin() ? yupClientProperty : null
    });
  }
  if (fieldsets.testuser) {
    yupObject.testuser = Yup.object().shape({
      date: yupDateProperty,
      client: isAdmin() ? yupClientProperty : null
    });
  }

  return (
    <>
      {!clients && <LoadingIndicator />}
      {clients && (
        <Formik initialValues={defaultValues} validationSchema={Yup.object(yupObject)} onSubmit={onSubmit}>
          {({ values, errors, touched, isSubmitting, handleChange, setFieldValue }) => (
            <Form autoComplete="off">
              {isAdmin() && (
                <>
                  <div className="field">
                    <input
                      type="checkbox"
                      id="ikobeCheck"
                      className="switch is-rounded is-info"
                      checked={fieldsets.ikobe}
                      onChange={e => {
                        updateFieldset("ikobe", e.target.checked);
                      }}
                    />{" "}
                    <label htmlFor="ikobeCheck" className="label">
                      IKOBE
                    </label>
                  </div>
                  <fieldset className="form-group" disabled={!fieldsets.ikobe}>
                    <div className="field">
                      <div className="field-body">
                        <div className="field">
                          <label className="label">von</label>
                          <div className="control">
                            <Field name="ikobe.date.from" type="date" className={`input${getIn(touched, "ikobe.date.from") && getIn(errors, "ikobe.date.from") ? " is-danger" : ""}`} />
                          </div>
                          <ErrorMessage component={ErrorLine} name="ikobe.date.from" />
                        </div>
                        <div className="field">
                          <label className="label">bis</label>
                          <div className="control">
                            <Field name="ikobe.date.until" type="date" className={`input${getIn(touched, "ikobe.date.until") && getIn(errors, "ikobe.date.until") ? " is-danger" : ""}`} />
                          </div>
                          <ErrorMessage component={ErrorLine} name="ikobe.date.until" />
                        </div>
                      </div>
                    </div>
                  </fieldset>
                  <hr />
                </>
              )}

              <div className="field">
                <input
                  type="checkbox"
                  id="licenceCheck"
                  className="switch is-rounded is-info"
                  checked={fieldsets.licences}
                  onChange={e => {
                    updateFieldset("licences", e.target.checked);
                  }}
                />{" "}
                <label htmlFor="licenceCheck" className="label">
                  Lizenzen
                </label>
                <p>Bitte wählen Sie einen Zeitraum aus, für den eine Übersicht (csv-Datei) über die von Ihnen verwaltete Menge an Lizenzen - tagesgenau aufgeschlüsselt - generiert werden soll.</p>
              </div>
              <fieldset className="form-group" disabled={!fieldsets.licences}>
                <div className="field">
                  <div className="field-body">
                    <div className="field">
                      <label className="label">von</label>
                      <div className="control">
                        <Field name="licences.date.from" type="date" className={`input${getIn(touched, "licences.date.from") && getIn(errors, "licences.date.from") ? " is-danger" : ""}`} />
                      </div>
                      <ErrorMessage component={ErrorLine} name="licences.date.from" />
                    </div>
                    <div className="field">
                      <label className="label">bis</label>
                      <div className="control">
                        <Field name="licences.date.until" type="date" className={`input${getIn(touched, "licences.date.until") && getIn(errors, "licences.date.until") ? " is-danger" : ""}`} />
                      </div>
                      <ErrorMessage component={ErrorLine} name="licences.date.until" />
                    </div>
                  </div>
                </div>

                {isAdmin() && (
                  <div className="field">
                    <div className="field-body">
                      <div className="field">
                        <label className="label">Mandant</label>
                        <div className="control">
                          <div className={`select${getIn(touched, "licences.client.type") && getIn(errors, "licences.client.type") ? " is-danger" : ""}`}>
                            <Field
                              as="select"
                              name="licences.client.type"
                              onChange={e => {
                                handleChange(e);
                                setFieldValue("licences.client.value", "");
                              }}>
                              {clientTypeSelect.map((item, index) => {
                                return (
                                  <option key={`ct-${index}`} value={item[0]}>
                                    {item[1]}
                                  </option>
                                );
                              })}
                            </Field>
                          </div>
                        </div>
                      </div>

                      {getIn(values, "licences.client.type") === "einer" && (
                        <div className="field">
                          <label className="label">Name</label>
                          <div className="control">
                            <div className={`select${getIn(touched, "licences.client.value") && getIn(errors, "licences.client.value") ? " is-danger" : ""}`}>
                              <Field as="select" name="licences.client.value">
                                <option value="">Wählen</option>
                                {clients.map((item, index) => {
                                  return (
                                    <option key={`cv-${index}`} value={item._id}>
                                      {item.name}
                                    </option>
                                  );
                                })}
                              </Field>
                            </div>
                          </div>
                        </div>
                      )}
                      {getIn(values, "licences.client.type") === "region" && (
                        <div className="field">
                          <label className="label">Bundesland</label>
                          <div className="control">
                            <div className={`select${getIn(touched, "licences.client.value") && getIn(errors, "licences.client.value") ? " is-danger" : ""}`}>
                              <Field as="select" name="licences.client.value">
                                <option value="">Wählen</option>
                                {formdata.federal_states.map((item, index) => {
                                  return (
                                    <option key={`cv-${index}`} value={item[0]}>
                                      {item[1]}
                                    </option>
                                  );
                                })}
                              </Field>
                            </div>
                          </div>
                        </div>
                      )}
                      {getIn(values, "licences.client.type") === "clientType" && (
                        <div className="field">
                          <label className="label">Mandaten-Typ</label>
                          <div className="control">
                            <div className={`select${getIn(touched, "licences.client.value") && getIn(errors, "licences.client.value") ? " is-danger" : ""}`}>
                              <Field as="select" name="licences.client.value">
                                <option value="">Wählen</option>
                                {formdata.client_types
                                  .filter(item => item[0].indexOf("-mandant") !== -1)
                                  .map((item, index) => {
                                    return (
                                      <option key={`cv-${index}`} value={item[0]}>
                                        {item[1]}
                                      </option>
                                    );
                                  })}
                              </Field>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <ErrorMessage component={ErrorLine} name="licences.client.type" />
                    <ErrorMessage component={ErrorLine} name="licences.client.value" />
                  </div>
                )}
              </fieldset>
              <hr />
              <div className="field">
                <input
                  type="checkbox"
                  id="testuserCheck"
                  className="switch is-rounded is-info"
                  checked={fieldsets.testuser}
                  onChange={e => {
                    updateFieldset("testuser", e.target.checked);
                  }}
                />{" "}
                <label htmlFor="testuserCheck" className="label">
                  Testnutzer
                </label>
                <p>Bitte wählen Sie einen Zeitraum aus, für den eine Auswertung (csv-Datei)  der soziodemografischen Daten von Testnutzern erfolgen soll.</p>
              </div>
              <fieldset className="form-group" disabled={!fieldsets.testuser}>
                <div className="field">
                  <div className="field-body">
                    <div className="field">
                      <label className="label">von</label>
                      <div className="control">
                        <Field name="testuser.date.from" type="date" className={`input${getIn(touched, "testuser.date.from") && getIn(errors, "testuser.date.from") ? " is-danger" : ""}`} />
                      </div>
                      <ErrorMessage component={ErrorLine} name="testuser.date.from" />
                    </div>
                    <div className="field">
                      <label className="label">bis</label>
                      <div className="control">
                        <Field name="testuser.date.until" type="date" className={`input${getIn(touched, "testuser.date.until") && getIn(errors, "testuser.date.until") ? " is-danger" : ""}`} />
                      </div>
                      <ErrorMessage component={ErrorLine} name="testuser.date.until" />
                    </div>
                  </div>
                </div>

                {isAdmin() && (
                  <div className="field">
                    <div className="field-body">
                      <div className="field">
                        <label className="label">Mandant</label>
                        <div className="control">
                          <div className={`select`}>
                            <Field
                              as="select"
                              name="testuser.client.type"
                              onChange={e => {
                                handleChange(e);
                                setFieldValue("testuser.client.value", "");
                              }}>
                              {clientTypeSelect.map((item, index) => {
                                return (
                                  <option key={`tct-${index}`} value={item[0]}>
                                    {item[1]}
                                  </option>
                                );
                              })}
                            </Field>
                          </div>
                        </div>
                      </div>
                      {getIn(values, "testuser.client.type") === "einer" && (
                        <div className="field">
                          <label className="label">Name</label>
                          <div className="control">
                            <div className={`select`}>
                              <Field as="select" name="testuser.client.value">
                                <option value="">Wählen</option>
                                {clients.map((item, index) => {
                                  return (
                                    <option key={`tcv-${index}`} value={item._id}>
                                      {item.name}
                                    </option>
                                  );
                                })}
                              </Field>
                            </div>
                          </div>
                        </div>
                      )}
                      {getIn(values, "testuser.client.type") === "region" && (
                        <div className="field">
                          <label className="label">Bundesland</label>
                          <div className="control">
                            <div className={`select`}>
                              <Field as="select" name="testuser.client.value">
                                <option value="">Wählen</option>
                                {formdata.federal_states.map((item, index) => {
                                  return (
                                    <option key={`tcv-${index}`} value={item[0]}>
                                      {item[1]}
                                    </option>
                                  );
                                })}
                              </Field>
                            </div>
                          </div>
                        </div>
                      )}
                      {getIn(values, "testuser.client.type") === "clientType" && (
                        <div className="field">
                          <label className="label">Mandaten-Typ</label>
                          <div className="control">
                            <div className={`select`}>
                              <Field as="select" name="testuser.client.value">
                                <option value="">Wählen</option>
                                {formdata.client_types
                                  .filter(item => item[0].indexOf("-mandant") !== -1)
                                  .map((item, index) => {
                                    return (
                                      <option key={`tcv-${index}`} value={item[0]}>
                                        {item[1]}
                                      </option>
                                    );
                                  })}
                              </Field>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <ErrorMessage component={ErrorLine} name="testuser.client.type" />
                    <ErrorMessage component={ErrorLine} name="testuser.client.value" />
                  </div>
                )}
              </fieldset>
              <hr />
              <div className="field">
                <div className="control field-body">
                  <button className="button is-success" type="submit" disabled={sendBtnDisabled(isSubmitting)}>
                    Absenden
                  </button>
                  <button className="button ml-2" type="button" onClick={props.onCancel}>
                    Abbrechen
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
}
export default FormStatistics;
